import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Send as Telegram, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAdmin } from '../context/AdminContext';
import AdminToolbar from './AdminToolbar';
import { EditableText } from './EditableComponents';
import WhatsAppIcon from './icons/WhatsAppIcon';

const Footer = () => {
  const { t, language } = useLanguage();
  const { isEditMode, getContent } = useAdmin();

  const quickLinks = [
    { path: '/', label: { ru: 'Главная', kz: 'Басты бет', en: 'Home', ar: 'الرئيسية' } },
    { path: '/about', label: { ru: 'О компании', kz: 'Компания туралы', en: 'About', ar: 'عن الشركة' } },
    { path: '/products', label: { ru: 'Продукция', kz: 'Өнімдер', en: 'Products', ar: 'المنتجات' } },
    { path: '/catalog', label: { ru: 'Каталог', kz: 'Каталог', en: 'Catalog', ar: 'الكتالوج' } },
    { path: '/installation', label: { ru: 'Монтаж', kz: 'Монтаж', en: 'Installation', ar: 'التركيب' } },
    { path: '/certificates', label: { ru: 'Сертификаты', kz: 'Сертификаттар', en: 'Certificates', ar: 'الشهادات' } },
    { path: '/contact', label: { ru: 'Контакты', kz: 'Байланыс', en: 'Contact', ar: 'تواصل معنا' } },
    { path: '/contact#route', label: { ru: 'Схема проезда', kz: 'Жол схемасы', en: 'Route to plant', ar: 'مسار الوصول' } }
  ];

  const products = [
    {
      label: { ru: 'Панели на основе ГКЛ', kz: 'ГКЛ негізіндегі панельдер', en: 'GKL-based panels', ar: 'ألواح على أساس GKL' },
      path: '/products/gkl'
    },
    {
      label: { ru: 'Панели Комфорт', kz: 'Комфорт панельдері', en: 'Comfort panels', ar: 'ألواح كومفورت' },
      path: '/products/comfort'
    },
    {
      label: { ru: 'Панели на основе СМЛ', kz: 'СМЛ негізіндегі панельдер', en: 'SML-based panels', ar: 'ألواح على أساس SML' },
      path: '/products/sml'
    }
  ];

  return (
    <footer className="bg-[#061d36] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <Link to="/" className="inline-flex items-center gap-2 mb-4 group">
              <img 
                src="/logo-icon-hq.png" 
                alt="GIPSOVINIL.KZ" 
                className="h-10 w-auto"
              />
              <img 
                src="/logo-text-hq.png" 
                alt="GIPSOVINIL.KZ" 
                className="h-5 w-auto"
              />
            </Link>
            <p className="text-blue-200 leading-relaxed mb-4">
              <EditableText
                section="footer"
                contentKey="about_text"
                defaultValue={t.footer.aboutText}
                language={language}
                className="inline"
              />
            </p>
            <a 
              href={`tel:${getContent('footer', 'phone1', language) || '+77472755088'}`}
              className="flex items-center gap-2 text-blue-200 hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              <EditableText
                section="footer"
                contentKey="phone1"
                defaultValue="+7 747 275 5088"
                language={language}
                className="inline"
              />
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-blue-200 hover:text-white hover:pl-1 transition-all inline-block"
                  >
                    {link.label[language]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.footer.products}</h4>
            <ul className="space-y-2">
              {products.map((product, index) => (
                <li key={index}>
                  <Link
                    to={product.path}
                    className="text-blue-200 hover:text-white hover:pl-1 transition-all inline-block"
                  >
                    {product.label[language]}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <Link
                  to="/catalog"
                  className="text-blue-400 hover:text-white font-medium transition-colors"
                >
                  {language === 'ru' ? 'Смотреть каталог →' : language === 'kz' ? 'Каталогты қарау →' : language === 'ar' ? 'عرض الكتالوج ←' : 'View catalog →'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Media & Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t.footer.social}</h4>
            <div className="flex space-x-3 mb-6">
              <a
                href="https://www.instagram.com/gipsovinil.kz/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg flex items-center justify-center transition-all hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://t.me/gipsovinilkz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#0088cc] hover:bg-[#0077b5] rounded-lg flex items-center justify-center transition-all hover:scale-110"
                aria-label="Telegram"
              >
                <Telegram className="w-5 h-5" />
              </a>
              <a
                href="https://wa.me/77717004430"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#25D366] hover:bg-[#20BD5A] rounded-lg flex items-center justify-center transition-all hover:scale-110"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon className="w-5 h-5" />
              </a>
            </div>
            
            <div className="text-sm text-blue-200 space-y-1">
              <p>
                <EditableText
                  section="footer"
                  contentKey="address"
                  defaultValue="СЭЗ Астана, Казахстан"
                  language={language}
                  className="inline"
                />
              </p>
              <a href={`mailto:${getContent('footer', 'email', language) || 'sales@gipsovinil.kz'}`} className="hover:text-white transition-colors block">
                <EditableText
                  section="footer"
                  contentKey="email"
                  defaultValue="sales@gipsovinil.kz"
                  language={language}
                  className="inline"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          {/* Legal Info */}
          <div className="mb-6 text-center md:text-left">
            <p className="text-blue-200/80 text-xs mb-2">
              {language === 'ru' ? 'ТОО «Гипсовинил» | БИН: 240140033968' :
               language === 'kz' ? '«Гипсовинил» ЖШС | БСН: 240140033968' :
               language === 'ar' ? 'شركة «جيبسوفينيل» ذات المسؤولية المحدودة | BIN: 240140033968' :
               'GIPSOVINIL LLP | BIN: 240140033968'}
            </p>
            <p className="text-blue-200/60 text-xs">
              {language === 'ru' ? 'Юр. адрес: Казахстан, г. Астана, район Алматы, ул. Бұйрат, здание 12' :
               language === 'kz' ? 'Заңды мекенжай: Қазақстан, Астана қ., Алматы ауданы, Бұйрат к-сі, 12 ғимарат' :
               language === 'ar' ? 'العنوان القانوني: كازاخستان، أستانا، منطقة ألماتي، شارع بويرات، مبنى 12' :
               'Legal address: Kazakhstan, Astana, Almaty district, Buyrat street, building 12'}
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-blue-200 text-sm">
              {t.footer.rights}
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <Link to="/privacy" className="text-blue-200 hover:text-white transition-colors">
                {language === 'ru' ? 'Политика конфиденциальности' : language === 'kz' ? 'Құпиялылық саясаты' : language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
              </Link>
              <Link to="/certificates" className="text-blue-200 hover:text-white transition-colors">
                {language === 'ru' ? 'Сертификаты' : language === 'kz' ? 'Сертификаттар' : language === 'ar' ? 'الشهادات' : 'Certificates'}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-3">
        <div className="container mx-auto px-4 flex justify-center">
          <AdminToolbar />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
